import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = {
  name?: string;
  email?: string;
  message?: string;
};

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [inquiryType, setInquiryType] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "Please enter your name.";
    if (!email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Please enter a valid email address.";
    if (!message.trim()) next.message = "Please enter a message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("contact-form-submit", {
        body: {
          name: name.trim(),
          email: email.trim(),
          inquiry_type: inquiryType ?? null,
          message: message.trim(),
        },
      });
      if (error) throw error;
      setSubmitted(true);
      setName("");
      setEmail("");
      setInquiryType(undefined);
      setMessage("");
      setErrors({});
    } catch (err) {
      const description =
        err instanceof Error
          ? err.message
          : "Please try again, or email info@legacylensai.com directly.";
      toast({
        title: "Could not send your message",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="px-4 py-4 flex justify-center">
        <Link to="/" className="opacity-70 hover:opacity-100 transition-opacity">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-24" />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground mb-3">
              Contact Us
            </h1>
            <p className="text-muted-foreground">
              Have a question about LegacyLens, want to discuss partnership opportunities, or interested in early access? We'd love to hear from you.
            </p>
          </div>

          {submitted ? (
            <div className="text-center bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl p-8">
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">
                Thanks — we'll be in touch shortly.
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                If you need to reach us directly, you can email{" "}
                <a
                  href="mailto:info@legacylensai.com"
                  className="text-primary hover:underline"
                >
                  info@legacylensai.com
                </a>
                .
              </p>
              <Link to="/" className="text-primary hover:underline text-sm">
                ← Back to home
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className="bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl p-6 sm:p-8 space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="inquiryType">Inquiry Type (optional)</Label>
                <Select value={inquiryType} onValueChange={setInquiryType}>
                  <SelectTrigger id="inquiryType">
                    <SelectValue placeholder="Select one (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Family">Family</SelectItem>
                    <SelectItem value="Partner">Partner</SelectItem>
                    <SelectItem value="Press">Press</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="Tell us a bit about what you're looking for..."
                  aria-invalid={!!errors.message}
                />
                {errors.message && (
                  <p className="text-sm text-destructive">{errors.message}</p>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                By submitting, you agree to our{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
                .
              </p>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          )}

          <div className="text-center mt-6">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
